import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { AdminViewTab } from "@/components/hub/AdminViewTab";
import { TeamRegistrationTab } from "@/components/hub/TeamRegistrationTab";
import { PaymentTrackingTab } from "@/components/hub/PaymentTrackingTab";
import { HubAdminDemoLayout } from "@/layout/HubAdminDemoLayout";

// ---------------------------------------------------------------------------
// DEMO MOCK DATA — not connected to any backend or live system
// ---------------------------------------------------------------------------

export const MOCK_TOURNAMENT = {
  id: "demo-tournament-1",
  name: "Sample Spring Tournament",
  dates: "Apr 24 to Apr 27, 2026",
  format: "30 Overs",
  registrationDeadline: "2026-04-10",
  depositDeadline: "2026-04-01",
  fees: {
    U11: { amount: 100, surface: "Astro Turf" },
    U13: { amount: 100, surface: "Astro Turf" },
    U15: { amount: 150, surface: "Turf" },
    U17: { amount: 150, surface: "Turf" },
  },
  depositAmount: 50,
  expectedTeams: 32,
};

export const MOCK_ACADEMIES = [
  "North Star Cricket Academy",
  "Lighthouse Cricket Club",
  "Riverside Cricket Academy",
  "Mountain View Cricket",
  "Harbor Cricket Club",
  "Sunset Cricket Academy",
  "Greenfield Cricket",
  "Westwind Cricket Academy",
  "Highland Cricket Club",
  "Lakeside Cricket Academy",
];

export type AgeGroup = "U11" | "U13" | "U15" | "U17";
export type PaymentStatus = "paid" | "deposit" | "unpaid";

export interface Registration {
  id: number;
  academy: string;
  ageGroup: AgeGroup;
  teamName: string;
  coachName: string;
  coachEmail: string;
  coachPhone: string;
  playerCount: number;
  amountDue: number;
  paid: number;
  status: PaymentStatus;
}

export const INITIAL_REGISTRATIONS: Registration[] = [
  {
    id: 1,
    academy: "North Star Cricket Academy",
    ageGroup: "U11",
    teamName: "North Star U11 Blue",
    coachName: "Coach One",
    coachEmail: "coach1@example.com",
    coachPhone: "555-0101",
    playerCount: 14,
    amountDue: 100,
    paid: 100,
    status: "paid",
  },
  {
    id: 2,
    academy: "Lighthouse Cricket Club",
    ageGroup: "U13",
    teamName: "Lighthouse U13 Red",
    coachName: "Coach Two",
    coachEmail: "coach2@example.com",
    coachPhone: "555-0102",
    playerCount: 13,
    amountDue: 100,
    paid: 50,
    status: "deposit",
  },
  {
    id: 3,
    academy: "Riverside Cricket Academy",
    ageGroup: "U15",
    teamName: "Riverside U15 Green",
    coachName: "Coach Three",
    coachEmail: "coach3@example.com",
    coachPhone: "555-0103",
    playerCount: 15,
    amountDue: 150,
    paid: 0,
    status: "unpaid",
  },
  {
    id: 4,
    academy: "Mountain View Cricket",
    ageGroup: "U17",
    teamName: "Mountain View U17 Gold",
    coachName: "Coach Four",
    coachEmail: "coach4@example.com",
    coachPhone: "555-0104",
    playerCount: 16,
    amountDue: 150,
    paid: 150,
    status: "paid",
  },
  {
    id: 5,
    academy: "Harbor Cricket Club",
    ageGroup: "U11",
    teamName: "Harbor U11 Stars",
    coachName: "Coach Five",
    coachEmail: "coach5@example.com",
    coachPhone: "555-0105",
    playerCount: 12,
    amountDue: 100,
    paid: 50,
    status: "deposit",
  },
  {
    id: 6,
    academy: "Sunset Cricket Academy",
    ageGroup: "U13",
    teamName: "Sunset U13 Thunder",
    coachName: "Coach Six",
    coachEmail: "coach6@example.com",
    coachPhone: "555-0106",
    playerCount: 14,
    amountDue: 100,
    paid: 0,
    status: "unpaid",
  },
  {
    id: 7,
    academy: "Greenfield Cricket",
    ageGroup: "U15",
    teamName: "Greenfield U15 Eagles",
    coachName: "Coach Seven",
    coachEmail: "coach7@example.com",
    coachPhone: "555-0107",
    playerCount: 15,
    amountDue: 150,
    paid: 50,
    status: "deposit",
  },
  {
    id: 8,
    academy: "Westwind Cricket Academy",
    ageGroup: "U17",
    teamName: "Westwind U17 Lions",
    coachName: "Coach Eight",
    coachEmail: "coach8@example.com",
    coachPhone: "555-0108",
    playerCount: 16,
    amountDue: 150,
    paid: 0,
    status: "unpaid",
  },
  {
    id: 9,
    academy: "Highland Cricket Club",
    ageGroup: "U11",
    teamName: "Highland U11 Knights",
    coachName: "Coach Nine",
    coachEmail: "coach9@example.com",
    coachPhone: "555-0109",
    playerCount: 13,
    amountDue: 100,
    paid: 100,
    status: "paid",
  },
  {
    id: 10,
    academy: "Lakeside Cricket Academy",
    ageGroup: "U13",
    teamName: "Lakeside U13 Falcons",
    coachName: "Coach Ten",
    coachEmail: "coach10@example.com",
    coachPhone: "555-0110",
    playerCount: 14,
    amountDue: 100,
    paid: 100,
    status: "paid",
  },
];

// ---------------------------------------------------------------------------

let nextId = INITIAL_REGISTRATIONS.length + 1;

const VALID_AGE_GROUPS: AgeGroup[] = ["U11", "U13", "U15", "U17"];

export default function HubTournamentDemo() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = ["admin", "registration", "payments"].includes(searchParams.get("tab") ?? "")
    ? (searchParams.get("tab") as string)
    : "admin";
  const ageGroupParam = VALID_AGE_GROUPS.includes(searchParams.get("ageGroup") as AgeGroup)
    ? (searchParams.get("ageGroup") as AgeGroup)
    : null;

  const [registrations, setRegistrations] = useState<Registration[]>(INITIAL_REGISTRATIONS);
  const displayedRegistrations = ageGroupParam
    ? registrations.filter((r) => r.ageGroup === ageGroupParam)
    : registrations;

  function addRegistration(entry: Omit<Registration, "id">) {
    setRegistrations((prev) => [...prev, { ...entry, id: nextId++ }]);
  }

  function updatePayment(id: number, paid: number, status: PaymentStatus) {
    setRegistrations((prev) =>
      prev.map((r) => (r.id === id ? { ...r, paid, status } : r))
    );
  }

  return (
    <HubAdminDemoLayout>
    <div data-testid="hub-tournament-demo-page">
      {/* Page header — tournament-specific context only */}
      <header className="bg-[#1E2761] text-white px-4 py-4 md:px-8">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl md:text-2xl font-bold">
              {MOCK_TOURNAMENT.name}
            </h1>
            {ageGroupParam && (
              <span className="mt-1 inline-block bg-blue-500 text-white text-xs font-semibold px-2 py-0.5 rounded">
                {ageGroupParam} only
              </span>
            )}
          </div>
          <div className="text-right text-sm text-blue-200 hidden sm:block">
            <p>{MOCK_TOURNAMENT.dates}</p>
            <p>{MOCK_TOURNAMENT.format}</p>
          </div>
        </div>
      </header>

      {/* Age group filter */}
      <div className="max-w-6xl mx-auto px-4 pt-4 md:px-8 flex items-center gap-2 flex-wrap">
        <span className="text-xs font-medium text-gray-500 mr-1">Age group:</span>
        {(["All", "U11", "U13", "U15", "U17"] as const).map((ag) => {
          const active = ag === "All" ? !ageGroupParam : ageGroupParam === ag;
          return (
            <button
              key={ag}
              onClick={() => {
                const tab = searchParams.get("tab");
                const next: Record<string, string> = {};
                if (tab) next.tab = tab;
                if (ag !== "All") next.ageGroup = ag;
                setSearchParams(next);
              }}
              className={`px-3 py-1 rounded-full text-sm font-semibold border transition-all ${
                active
                  ? "bg-[#1E2761] text-white border-[#1E2761]"
                  : "bg-white text-gray-600 border-gray-300 hover:border-[#1E2761] hover:text-[#1E2761]"
              }`}
            >
              {ag}
            </button>
          );
        })}
      </div>

      {/* Main content */}
      <main className="max-w-6xl mx-auto px-4 py-6 md:px-8">
        <Tabs defaultValue={initialTab} className="w-full">
          <TabsList className="mb-6 w-full justify-start gap-1 bg-white border border-gray-200 rounded-lg p-1 h-auto flex-wrap">
            <TabsTrigger
              value="admin"
              className="data-testid-admin-tab"
              data-testid="tab-admin"
            >
              Admin View
            </TabsTrigger>
            <TabsTrigger value="registration" data-testid="tab-registration">
              Team Registration
            </TabsTrigger>
            <TabsTrigger value="payments" data-testid="tab-payments">
              Payment Tracking
            </TabsTrigger>
          </TabsList>

          <TabsContent value="admin">
            <AdminViewTab tournament={MOCK_TOURNAMENT} registrations={displayedRegistrations} />
          </TabsContent>

          <TabsContent value="registration">
            <TeamRegistrationTab
              tournament={MOCK_TOURNAMENT}
              academies={MOCK_ACADEMIES}
              registrations={displayedRegistrations}
              onAdd={addRegistration}
            />
          </TabsContent>

          <TabsContent value="payments">
            <PaymentTrackingTab
              registrations={displayedRegistrations}
              onUpdatePayment={updatePayment}
            />
          </TabsContent>
        </Tabs>
      </main>
    </div>
    </HubAdminDemoLayout>
  );
}
